import React, { useState, useRef } from "react";
import { motion, AnimatePresence, useDragControls } from "framer-motion";
import { MessageSquare } from "lucide-react";
import { useChat } from "@/hooks/useChat";
import { ChatHeader } from "./chat/ChatHeader";
import { ChatMessages } from "./chat/ChatMessages";
import { ChatComposer } from "./chat/ChatComposer";

const LAUNCHER_MOTION = {
  initial: { scale: 0, opacity: 0 },
  animate: { scale: 1, opacity: 1 },
  exit: { scale: 0, opacity: 0 },
  whileHover: { scale: 1.05 },
  whileTap: { scale: 0.95 },
  whileDrag: { scale: 1.1, cursor: "grabbing" },
};

const PANEL_MOTION = {
  initial: { opacity: 0, y: 24, scale: 0.96 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: 24, scale: 0.96 },
  transition: { duration: 0.25, ease: [0.22, 1, 0.36, 1] },
};

// Draggable AI support widget: launcher bubble + chat panel (header is the drag handle).
export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const dragBoundsRef = useRef(null);
  const dragControls = useDragControls();
  const { messages, loading, quota, send, clear } = useChat(open);

  return (
    <div ref={dragBoundsRef} className="fixed inset-0 z-[60] pointer-events-none">
      <AnimatePresence>
        {!open && (
          <motion.button
            key="launcher"
            data-testid="chat-launcher"
            onClick={() => setOpen(true)}
            drag
            dragConstraints={dragBoundsRef}
            dragMomentum={false}
            dragElastic={0.08}
            {...LAUNCHER_MOTION}
            className="pointer-events-auto absolute bottom-5 left-5 h-14 w-14 rounded-full grid place-items-center bg-[#04162E] text-white shadow-xl shadow-black/30 border border-white/15 cursor-grab touch-none"
            aria-label="Open AI support chat (drag to move)"
          >
            <MessageSquare className="h-6 w-6" />
            <span className="absolute -top-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-[#0E8A57] border-2 border-[#F4F6F3]" />
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <motion.div
            key="panel"
            data-testid="chat-panel"
            drag
            dragListener={false}
            dragControls={dragControls}
            dragConstraints={dragBoundsRef}
            dragMomentum={false}
            dragElastic={0.04}
            {...PANEL_MOTION}
            className="pointer-events-auto absolute bottom-5 left-5 right-5 sm:right-auto sm:w-[380px] h-[560px] max-h-[80vh] flex flex-col rounded-[2px] overflow-hidden border border-[rgba(4,22,46,0.14)] bg-white shadow-2xl shadow-black/25"
          >
            <ChatHeader onDragStart={(e) => dragControls.start(e)} onClear={clear} onClose={() => setOpen(false)} />
            <ChatMessages messages={messages} loading={loading} onPick={send} />
            <ChatComposer loading={loading} quota={quota} onSend={send} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
